import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaSearch, FaTimes, FaBriefcase, FaFileAlt, FaBrain, FaToggleOn, FaToggleOff, FaRegLightbulb } from "react-icons/fa";
import { postData } from "../../services/FetchNodeservices";
import TalentIntelligence from "./TalentIntelligence";

export default function TalentPool() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Page tabs: "search" | "insights"
  const [activeTab, setActiveTab] = useState("search");

  // Search modes: "form" | "ai"
  const [searchSubTab, setSearchSubTab] = useState("form");
  const [aiQuery, setAiQuery] = useState("");
  const [parsingAi, setParsingAi] = useState(false);

  // Enhanced search options
  const [booleanOn, setBooleanOn] = useState(false);
  const [mandatoryKeywords, setMandatoryKeywords] = useState(false);
  const [includeSimilarDesignation, setIncludeSimilarDesignation] = useState(false);

  // Keyword suggestions
  const [keywordSuggestions] = useState([
    "Java Full Stack Developer", "Spring Boot", "React.js", "Node.js", "AWS", "Python", "SQL", "Selenium", "DevOps"
  ]);

  // Recent searches state
  const [recentConversationalQueries, setRecentConversationalQueries] = useState([]);
  const [recentKeywords, setRecentKeywords] = useState([]);

  useEffect(() => {
    try {
      const savedConversations = JSON.parse(localStorage.getItem("recentConversationalQueries")) || [];
      const savedKeywords = JSON.parse(localStorage.getItem("recentKeywords")) || [];
      setRecentConversationalQueries(savedConversations);
      setRecentKeywords(savedKeywords);
    } catch (e) {
      console.error("Error reading from localStorage:", e);
    }
  }, []);

  const [filters, setFilters] = useState({
    q: "",
    name: "",
    expMin: "",
    expMax: "",
    qualification: "",
    designation: "",
    company: "",
    city: "",
    state: "",
    country: "",
    notice_period: "",
    ctcMin: "",
    ctcMax: "",
  });

  useEffect(() => {
    const newFilters = {};
    for (const key of Object.keys(filters)) {
      newFilters[key] = searchParams.get(key) || "";
    }
    setFilters(newFilters);
    // eslint-disable-next-line
  }, [searchParams]);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    for (const [key, val] of Object.entries(filters)) {
      if (val.trim() !== "") {
        params.append(key, val.trim());
      }
    }

    // Save search inputs to recent keywords
    const extractedKeywords = [];
    if (filters.q.trim()) {
      const rawTokens = filters.q.split(/[,;&|]|\bAND\b|\bOR\b|\bNOT\b/gi);
      rawTokens.forEach(t => {
        const cleaned = t.replace(/['"()]/g, "").trim();
        if (cleaned.length > 1) extractedKeywords.push(cleaned);
      });
    }
    ["designation", "company", "city", "qualification", "industry", "institute"].forEach(key => {
      const val = filters[key]?.trim();
      if (val && val.length > 1) {
        extractedKeywords.push(val);
      }
    });

    if (extractedKeywords.length > 0) {
      try {
        let updatedKeywords = [...recentKeywords];
        extractedKeywords.forEach(token => {
          updatedKeywords = [token, ...updatedKeywords.filter(k => k.toLowerCase() !== token.toLowerCase())];
        });
        updatedKeywords = updatedKeywords.slice(0, 10);
        setRecentKeywords(updatedKeywords);
        localStorage.setItem("recentKeywords", JSON.stringify(updatedKeywords));
      } catch (err) {
        console.error("Failed to save keywords to localStorage:", err);
      }
    }

    if (includeSimilarDesignation) {
      params.append("similar_designation", "true");
    }
    if (booleanOn) {
      params.append("boolean_logic", "true");
    }
    navigate(`/dashboard/talentpool/results?${params.toString()}`);
  };

  const handleAiSearchSubmit = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    // Save conversational query to recent list
    try {
      let updatedConversations = [aiQuery.trim(), ...recentConversationalQueries.filter(q => q.toLowerCase() !== aiQuery.trim().toLowerCase())];
      updatedConversations = updatedConversations.slice(0, 6);
      setRecentConversationalQueries(updatedConversations);
      localStorage.setItem("recentConversationalQueries", JSON.stringify(updatedConversations));
    } catch (err) {
      console.error("Failed to save conversational query to localStorage:", err);
    }

    try {
      setParsingAi(true);
      let res = await postData("api/talentpool/parse-ai", { query: aiQuery.trim() });
      if (!res || res.status === undefined) {
        res = await postData("talentpool/parse-ai", { query: aiQuery.trim() });
      }
      if (res?.status && res?.data) {
        const params = new URLSearchParams();
        for (const [key, val] of Object.entries(res.data)) {
          if (val && String(val).trim() !== "") {
            params.append(key, String(val).trim());
          }
        }
        if (includeSimilarDesignation) {
          params.append("similar_designation", "true");
        }
        navigate(`/dashboard/talentpool/results?${params.toString()}`);
      } else {
        navigate(`/dashboard/talentpool/results?q=${encodeURIComponent(aiQuery.trim())}`);
      }
    } catch (err) {
      console.error("AI parse search error:", err);
      navigate(`/dashboard/talentpool/results?q=${encodeURIComponent(aiQuery.trim())}`);
    } finally {
      setParsingAi(false);
    }
  };

  const appendKeyword = (tag) => {
    const currentQ = filters.q.trim();
    if (!currentQ) {
      setFilters({ ...filters, q: tag });
    } else {
      const joiner = booleanOn ? " AND " : ", ";
      setFilters({ ...filters, q: `${currentQ}${joiner}${tag}` });
    }
  };

  const clearFilters = () => {
    setFilters({
      q: "",
      name: "",
      expMin: "",
      expMax: "",
      qualification: "",
      designation: "",
      company: "",
      city: "",
      state: "",
      country: "",
      notice_period: "",
      ctcMin: "",
      ctcMax: "",
    });
    setAiQuery("");
    setBooleanOn(false);
    setMandatoryKeywords(false);
    setIncludeSimilarDesignation(false);
  };

  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans p-2">
      {/* PAGE LEVEL NAVIGATION TABS */}
      <div className="flex border-b border-gray-200 dark:border-slate-800 gap-4 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab("search")}
          className={`pb-3 text-xs font-black uppercase tracking-wider border-b-2 transition duration-200 ${
            activeTab === "search"
              ? "border-indigo-650 text-indigo-600 border-indigo-600"
              : "border-transparent text-gray-400 hover:text-gray-600"
          }`}
        >
          Talent Search
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("insights")}
          className={`pb-3 text-xs font-black uppercase tracking-wider border-b-2 transition duration-200 flex items-center gap-1.5 ${
            activeTab === "insights"
              ? "border-indigo-650 text-indigo-600 border-indigo-600"
              : "border-transparent text-gray-400 hover:text-gray-600"
          }`}
        >
          <FaBrain className="w-3.5 h-3.5" /> Market Intelligence & Salary Insights
        </button>
      </div>

      {activeTab === "insights" ? (
        <TalentIntelligence />
      ) : (
        <>
          {/* HEADER */}
          <div className="space-y-1.5 mt-2">
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">
              Find Top Talent
            </h1>
            <p className="text-xs font-semibold text-gray-400 max-w-2xl">
              Perform high-performance Boolean searches or conversational AI queries across applicant profiles, designations, and locations.
            </p>
          </div>

          {/* FORM CARD */}
          <div className="bg-white border border-gray-100 rounded-[32px] p-8 shadow-2xl shadow-indigo-100/10 w-full space-y-6">
            {/* SUB-TABS (Form vs AI Assist) */}
            <div className="flex gap-1.5 p-1 bg-gray-50 rounded-2xl border border-gray-100/50 max-w-[280px]">
              <button
                type="button"
                onClick={() => setSearchSubTab("form")}
                className={`flex-1 text-center py-2 text-[10px] font-black uppercase tracking-wider rounded-xl transition duration-200 ${
                  searchSubTab === "form"
                    ? "bg-white text-indigo-600 shadow-sm border border-gray-150"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                Search Form
              </button>
              <button
                type="button"
                onClick={() => setSearchSubTab("ai")}
                className={`flex-1 text-center py-2 text-[10px] font-black uppercase tracking-wider rounded-xl transition duration-200 flex items-center justify-center gap-1 ${
                  searchSubTab === "ai"
                    ? "bg-white text-indigo-600 shadow-sm border border-gray-150"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                AI Assist <span className="text-[8px] font-black px-1 py-0.2 bg-indigo-50 text-indigo-600 rounded">BETA</span>
              </button>
            </div>

            {searchSubTab === "ai" ? (
              /* CONVERSATIONAL AI ASSIST SEARCH */
              <form onSubmit={handleAiSearchSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-indigo-950 uppercase tracking-widest flex items-center gap-1.5">
                    <FaBrain className="text-indigo-600" /> Conversational Sourcing Query
                  </label>
                  <textarea
                    rows={4}
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    placeholder='e.g. Help me find a product manager with 4 years of work experience and based out of Mumbai'
                    className="w-full p-4 rounded-2xl bg-[#f4f6fa]/60 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium resize-none shadow-inner"
                  />
                  <div className="text-[10px] text-gray-400 font-semibold flex flex-wrap gap-2 items-center">
                    <span className="font-extrabold text-indigo-900 flex items-center gap-0.5"><FaRegLightbulb /> Suggested Prompts:</span>
                    {recentConversationalQueries.length > 0 ? (
                      recentConversationalQueries.map((queryText, index) => {
                        const displayLabel = queryText.length > 40 ? queryText.slice(0, 38) + "..." : queryText;
                        return (
                          <button
                            type="button"
                            key={index}
                            onClick={() => setAiQuery(queryText)}
                            className="px-2.5 py-1 bg-indigo-50/50 border border-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-100 hover:text-indigo-800 transition font-bold"
                          >
                            "{displayLabel}"
                          </button>
                        );
                      })
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setAiQuery("Help me find a node developer with 3 years of work experience in Pune")}
                          className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition"
                        >
                          "Node developer, 3y exp, Pune"
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiQuery("Looking for a manager based out of Mumbai")}
                          className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition"
                        >
                          "Manager in Mumbai"
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiQuery("Help me find a product manager with 4 years of work experience and based out of Mumbai")}
                          className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition"
                        >
                          "Product Manager, 4y exp, Mumbai"
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-2xl text-xs font-bold transition"
                  >
                    Clear Query
                  </button>
                  <button
                    type="submit"
                    disabled={parsingAi}
                    className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-bold shadow-lg shadow-indigo-200 transition flex items-center gap-2 disabled:opacity-50"
                  >
                    {parsingAi ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Parsing Intent...
                      </>
                    ) : (
                      <>
                        <FaSearch className="w-3 h-3" /> Search with AI
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              /* STANDARD SEARCH FORM */
              <form onSubmit={handleSearchSubmit} className="space-y-6">
                
                {/* BOOLEAN KEYWORD SEARCH (MAIN INPUT) */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-black text-indigo-950 uppercase tracking-widest block">
                      Keywords / Boolean Query
                    </label>

                    {/* Boolean Options Switchers */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => setBooleanOn(!booleanOn)}>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Boolean Logic</span>
                        {booleanOn ? (
                          <FaToggleOn className="w-5.5 h-5.5 text-indigo-600 transition" />
                        ) : (
                          <FaToggleOff className="w-5.5 h-5.5 text-gray-300 transition" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          id="mandatoryKeywords"
                          checked={mandatoryKeywords}
                          onChange={(e) => setMandatoryKeywords(e.target.checked)}
                          className="w-3.5 h-3.5 border-gray-200 rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <label htmlFor="mandatoryKeywords" className="text-[10px] font-bold text-gray-400 uppercase tracking-wider cursor-pointer">
                          Make all keywords mandatory
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      name="q"
                      value={filters.q}
                      onChange={handleChange}
                      placeholder={booleanOn ? 'e.g. (Java AND React) NOT Python' : 'e.g. Java, React, Python'}
                      className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-[#f4f6fa]/60 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium"
                    />
                  </div>

                  {/* AI Suggested Keywords */}
                  <div className="text-[10px] text-gray-400 font-semibold flex flex-wrap gap-2 items-center pt-1.5">
                    <span className="font-extrabold text-indigo-900 flex items-center gap-0.5"><FaRegLightbulb /> AI Suggested:</span>
                    
                    {/* Render recent search keywords first */}
                    {recentKeywords.map((tag, index) => (
                      <button
                        type="button"
                        key={`recent-${index}`}
                        onClick={() => appendKeyword(tag)}
                        className="px-2 py-0.5 bg-indigo-50/50 border border-indigo-150 rounded-lg hover:bg-indigo-100 hover:text-indigo-800 hover:border-indigo-200 transition font-bold"
                      >
                        + {tag}
                      </button>
                    ))}
                    
                    {/* Render default keyword suggestions as fallback/complements */}
                    {keywordSuggestions
                      .filter(tag => !recentKeywords.some(rk => rk.toLowerCase() === tag.toLowerCase()))
                      .slice(0, Math.max(2, 9 - recentKeywords.length))
                      .map(tag => (
                        <button
                          type="button"
                          key={tag}
                          onClick={() => appendKeyword(tag)}
                          className="px-2 py-0.5 bg-gray-50 border border-gray-150 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition"
                        >
                          + {tag}
                        </button>
                      ))}
                  </div>

                  {booleanOn && (
                    <div className="text-[10px] text-indigo-700 font-semibold flex flex-wrap gap-x-4 gap-y-1 bg-indigo-50/30 p-3 rounded-2xl border border-indigo-100/50 mt-2">
                      <span className="font-extrabold text-indigo-900">Boolean Tips:</span>
                      <span><b>AND</b> / <b>OR</b> / <b>NOT</b> (e.g. <i>React AND Node</i>)</span>
                      <span>Parentheses <b>( )</b> (e.g. <i>(Java OR C#) NOT QA</i>)</span>
                      <span>Phrases <b>" "</b> (e.g. <i>"Senior Architect"</i>)</span>
                    </div>
                  )}
                </div>

                <hr className="border-gray-100" />

                {/* CANDIDATE INFO SECTION */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-gray-50 pb-2">
                      <FaFileAlt className="text-indigo-500" /> Candidate Profile
                    </h3>
                    
                    <Input
                      label="Candidate Name"
                      name="name"
                      value={filters.name}
                      onChange={handleChange}
                      placeholder="e.g. Abhishek"
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Experience Min (Yrs)"
                        name="expMin"
                        type="number"
                        value={filters.expMin}
                        onChange={handleChange}
                        placeholder="Min"
                      />
                      <Input
                        label="Experience Max (Yrs)"
                        name="expMax"
                        type="number"
                        value={filters.expMax}
                        onChange={handleChange}
                        placeholder="Max"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Min CTC (Lacs)"
                        name="ctcMin"
                        type="number"
                        value={filters.ctcMin}
                        onChange={handleChange}
                        placeholder="Min"
                      />
                      <Input
                        label="Max CTC (Lacs)"
                        name="ctcMax"
                        type="number"
                        value={filters.ctcMax}
                        onChange={handleChange}
                        placeholder="Max"
                      />
                    </div>

                    <Input
                      label="Notice Period (Days)"
                      name="notice_period"
                      value={filters.notice_period}
                      onChange={handleChange}
                      placeholder="e.g. 30"
                    />
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-gray-50 pb-2">
                      <FaBriefcase className="text-indigo-500" /> Work & Location
                    </h3>

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Designation</p>
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            id="similarDesignation"
                            checked={includeSimilarDesignation}
                            onChange={(e) => setIncludeSimilarDesignation(e.target.checked)}
                            className="w-3.5 h-3.5 border-gray-200 rounded text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="similarDesignation" className="text-[9px] font-bold text-gray-400 uppercase tracking-wider cursor-pointer">
                            Include similar designations
                          </label>
                        </div>
                      </div>
                      <input
                        type="text"
                        name="designation"
                        value={filters.designation}
                        onChange={handleChange}
                        placeholder="e.g. Developer"
                        className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                      />
                    </div>

                    <Input
                      label="Company"
                      name="company"
                      value={filters.company}
                      onChange={handleChange}
                      placeholder="e.g. Morgan Placement"
                    />

                    <Input
                      label="City"
                      name="city"
                      value={filters.city}
                      onChange={handleChange}
                      placeholder="e.g. Mumbai"
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="State"
                        name="state"
                        value={filters.state}
                        onChange={handleChange}
                        placeholder="e.g. Maharashtra"
                      />
                      <Input
                        label="Country"
                        name="country"
                        value={filters.country}
                        onChange={handleChange}
                        placeholder="e.g. India"
                      />
                    </div>
                    
                    <Input
                      label="Highest Qualification"
                      name="qualification"
                      value={filters.qualification}
                      onChange={handleChange}
                      placeholder="e.g. B.Tech"
                    />
                  </div>
                </div>

                {/* ACTIONS */}
                <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-2xl text-xs font-bold transition flex items-center gap-1.5"
                  >
                    <FaTimes /> Clear Form
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-bold shadow-lg shadow-indigo-200 transition flex items-center gap-2"
                  >
                    <FaSearch className="w-3 h-3" /> Search Resumes
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">{label}</p>
      <input
        {...props}
        className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
      />
    </div>
  );
}
